import { useState, useRef, useEffect } from "react";
import type { LangCode, SeasonFilter } from "../data/types.js";
import { t } from "../i18n/strings.js";

const PILL_STYLE = {
  background: "rgba(255,255,255,0.88)",
  backdropFilter: "blur(16px) saturate(1.4)",
  WebkitBackdropFilter: "blur(16px) saturate(1.4)",
  boxShadow: "0 2px 12px rgba(26,25,23,0.08)",
} as const;

interface Option {
  value: SeasonFilter;
  i18nKey: string;
  bg: string;
  text: string;
}

const OPTIONS: Option[] = [
  { value: "all", i18nKey: "filter.all", bg: "", text: "" },
  {
    value: "day",
    i18nKey: "filter.day",
    bg: "rgb(255, 243, 214)",
    text: "rgb(161, 110, 23)",
  },
  {
    value: "night",
    i18nKey: "filter.night",
    bg: "rgb(224, 222, 248)",
    text: "rgb(79, 70, 150)",
  },
  {
    value: "sakura",
    i18nKey: "filter.sakura",
    bg: "rgb(252, 228, 236)",
    text: "rgb(168, 56, 93)",
  },
  {
    value: "momiji",
    i18nKey: "filter.momiji",
    bg: "rgb(255, 232, 214)",
    text: "rgb(166, 80, 26)",
  },
  {
    value: "hot",
    i18nKey: "filter.hot",
    bg: "rgb(255, 228, 225)",
    text: "rgb(180, 50, 50)",
  },
];

interface Props {
  filter: SeasonFilter;
  onFilterChange: (f: SeasonFilter) => void;
  lang: LangCode;
}

export function FilterDropdown({ filter, onFilterChange, lang }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const current = OPTIONS.find((o) => o.value === filter) ?? OPTIONS[0];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-30 pointer-events-none"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div className="mx-4 mt-3 flex items-center gap-2 pointer-events-auto">
        <a
          href="https://kimonorentalann.com"
          target="_top"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-2xl no-underline flex-shrink-0"
          style={PILL_STYLE}
        >
          <span className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold" style={{ color: "#B76E6E" }}>
              Kimono Rental Ann
            </span>
            <span className="text-[10px] text-ink-2">
              {t("header.subtitle", lang)}
            </span>
          </span>
        </a>

        <div className="relative" ref={ref}>
          <button
            onClick={() => setOpen(!open)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-[12px] font-semibold transition-all shadow-sm"
            style={
              filter !== "all"
                ? { ...PILL_STYLE, background: current.bg, color: current.text }
                : PILL_STYLE
            }
          >
            {t(current.i18nKey, lang)}
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform ${open ? "rotate-180" : ""}`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {open && (
            <div
              className="absolute top-full left-0 mt-2 rounded-2xl py-1.5 min-w-[180px]"
              style={PILL_STYLE}
            >
              {OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    onFilterChange(opt.value);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] font-medium transition-all ${
                    filter === opt.value ? "" : "text-ink-1 active:bg-surface-1"
                  }`}
                  style={
                    filter === opt.value && opt.text
                      ? { color: opt.text }
                      : undefined
                  }
                >
                  <span>{t(opt.i18nKey, lang)}</span>
                  {filter === opt.value && (
                    <span
                      className="text-sm"
                      style={opt.text ? { color: opt.text } : undefined}
                    >
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
